/*
 * Shared by the performer views that add, edit and import songs (ENG-796):
 * the add-song page, the song list's edit modal and the CSV import results.
 *
 * A song the catalogue lookup could not find can still be added "anyway"
 * (ENG-795). These views never name the lookup's provider, and never show the
 * server's own wording of a lookup failure: every error code is mapped to a
 * catalog message here. Listener views never read anything from this module.
 */

/** "Rock, Folk ,, Indie" -> ['Rock', 'Folk', 'Indie']. */
export const parseGenres = (text) =>
  String(text ?? '')
    .split(',')
    .map((genre) => genre.trim())
    .filter(Boolean);

/**
 * `text` (a server's own wording, already judged safe to show) unless it
 * names the song lookup's provider, which the performer views never do
 * outside lyrics attribution; '' then, so the caller's catalog message is
 * shown instead.
 */
export const withoutProviderName = (text) =>
  typeof text === 'string' && !/musixmatch/i.test(text) ? text : '';

/** The not-found answer of an add or a match (422 `song_not_in_catalogue`). */
export const NOT_IN_CATALOGUE = 'song_not_in_catalogue';

/**
 * Codes for a lookup that could not be made at all. None of them is a
 * not-found, so "Add it anyway" is never offered for one: the band is asked
 * to try again (or to check the title and artist) instead.
 */
export const LOOKUP_FAILURE_CODES = Object.freeze([
  'song_lookup_rejected',
  'song_lookup_rate_limited',
  'song_lookup_unavailable',
  'song_lookup_daily_limit',
  'song_lookup_paused',
  'song_busy',
]);

/** True when `error` is the add's or the match's not-found answer. */
export const isNotInCatalogue = (error) => error?.code === NOT_IN_CATALOGUE;

/** The `{ title, artist }` a not-found answer offers for "Did you mean ...?", or null. */
export const closestOf = (value) => {
  const closest = value?.closest;
  if (!closest || typeof closest !== 'object') return null;
  const title = typeof closest.title === 'string' ? closest.title.trim() : '';
  const artist = typeof closest.artist === 'string' ? closest.artist.trim() : '';
  return title && artist ? { title, artist } : null;
};

const minutesUntilRetry = (error) => {
  const seconds = Number(error?.params?.retry_after_seconds);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds / 60) : null;
};

/**
 * The catalog message for a failed song lookup, add or match, or null when
 * `error` is not one this module words (the caller then falls back to its
 * own message). Never the server's text.
 */
export const songLookupErrorMessage = (error, t) => {
  switch (error?.code) {
    case 'song_lookup_rejected':
      return t('songLookup.errors.rejected');
    case 'song_lookup_rate_limited': {
      const minutes = minutesUntilRetry(error);
      return minutes ? t('songLookup.errors.rateLimitedFor', { count: minutes }) : t('songLookup.errors.rateLimited');
    }
    case 'song_lookup_daily_limit':
      return t('songLookup.errors.dailyLimit');
    case 'song_lookup_paused':
      return t('songLookup.errors.paused');
    case 'song_lookup_unavailable':
    case 'song_busy':
      return t('songLookup.errors.unavailable');
    case 'song_title_required':
      return t('addSong.errors.titleRequired');
    case 'song_artist_required':
      return t('addSong.errors.titleArtistRequired');
    case 'song_album_too_long':
      return t('songLookup.errors.albumTooLong');
    case 'invalid_release_date':
      return t('songLookup.errors.invalidReleaseDate');
    default:
      break;
  }
  // The edge rate limiter answers 429 without a code; a 503 without one is
  // a gateway's. Both are "try again", never "add it anyway".
  if (error?.status === 429 || error?.status === 503) return t('songLookup.errors.unavailable');
  return null;
};
